import { createContext, useContext } from 'react';
import { useMatch } from 'react-router-dom';

import { noop } from '@utils/browser';

import { useJourneyCached, useUpdateJourneyUserCollapse } from 'api/journeys/hooks';
import { JourneyAction } from 'api/types';

import { JourneyNode } from '../node-types/types';

type GetActionsToHideProps = {
  actions: Array<JourneyAction>;
  collapsedActions: Array<JourneyAction['shortId']>;
};

export const getNodesToHide = ({ actions, collapsedActions }: GetActionsToHideProps) => {
  const nodesToHide: Set<JourneyAction['shortId']> = new Set();

  const addChildActions = (
    childNodeId: JourneyAction['shortId'],
    shouldSkipAdd: boolean = false,
  ) => {
    if (!shouldSkipAdd) {
      nodesToHide.add(childNodeId);
    }
    const childActions = actions.filter((action) => action.parentAction?.shortId === childNodeId);
    childActions.forEach((action) => {
      addChildActions(action.shortId);
    });
  };

  collapsedActions.forEach((collapsedAction) => {
    addChildActions(collapsedAction, true);
  });

  return nodesToHide;
};

type CollapseContextProps = {
  expandAction(id: JourneyAction['shortId']): void;
  collapseAction(id: JourneyAction['shortId']): void;
};

export const CollapseContext = createContext<CollapseContextProps>({
  expandAction: noop,
  collapseAction: noop,
});

export const useCollapseContext = () => {
  const collapseContext = useContext(CollapseContext);
  return collapseContext;
};

export const useCollapsedActions = () => {
  const params = useMatch('/workspaces/:workspaceId/journeys/:journeyId/*')?.params;
  const journeyId = params?.journeyId ?? '';
  const journey = useJourneyCached(journeyId);

  const collapsedActions = new Set(journey?.collapsedActions ?? []);

  const updateUserCollapse = useUpdateJourneyUserCollapse(journeyId);

  const expandAction = (id: JourneyNode['id']) => {
    const newActions = new Set(collapsedActions);
    newActions.delete(id);
    updateUserCollapse(newActions);
  };

  const collapseAction = (id: JourneyNode['id']) => {
    const newActions = new Set(collapsedActions);
    newActions.add(id);
    updateUserCollapse(newActions);
  };

  return { expandAction, collapseAction };
};
